import React, { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'
import { USER_ROLES, ROLE_CONFIG, COURSES, HOMEWORKS } from '../data/index.js'
import { useTheme } from '../hooks/useTheme'
import HomeworkCard from '../components/HomeworkCard.jsx'
import { learnApi } from '../services/api'

const WEEK_DAYS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

export default function LearnPage() {
  const { navigate, showToast, checkedIn, setCheckedIn, userRole, homeworks, fetchHomeworks, myCourses, fetchMyCourses, user } = useApp()
  const { bgPrimary, bgWhite, textPrimary, textSecondary, textMuted, borderColor, shadowColor, progressBg, isDark } = useTheme()

  const [stats, setStats] = useState(null)

  // 进入学习中心时加载数据
  useEffect(() => {
    if (homeworks.length === 0) fetchHomeworks()
    fetchMyCourses()
  }, [userRole])

  // 加载学习统计（云端数据）
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await learnApi.getStats()
        setStats(res)
      } catch (e) {
        console.warn('获取学习统计失败', e)
      }
    }
    loadStats()
  }, [userRole])

  // 优先使用云端数据，未登录时使用本地数据
  const myCoursesData = myCourses.length > 0 ? myCourses : COURSES.filter(c => c.progress > 0)
  const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1

  const isTeacher = userRole === USER_ROLES.TEACHER
  const isStudent = userRole === USER_ROLES.STUDENT
  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG[USER_ROLES.STUDENT]

  // 优先使用云端数据，未登录时使用本地数据
  const homeworksData = homeworks.length > 0 ? homeworks : HOMEWORKS

  // 作业统计
  const now = new Date()
  const pendingHomeworks = isStudent
    ? homeworksData.filter(h => !h.mySubmission && new Date(h.deadline) >= now)
    : homeworksData

  const submittedCount = isStudent
    ? homeworksData.filter(h => !!h.mySubmission).length
    : homeworksData.reduce((acc, h) => acc + (h.submission_count || 0), 0)

  const gradedCount = isStudent
    ? homeworksData.filter(h => h.mySubmission?.grade).length
    : homeworksData.reduce((acc, h) => acc + (h.graded_count || 0), 0)

  return (
    <div className="flex flex-col h-full" style={{ background: bgPrimary }}>
      {/* 顶栏 - 适配刘海屏 */}
      <div className="flex-shrink-0 px-4 header-height safe-area-top flex items-center justify-between"
           style={{ background: bgWhite, boxShadow: '0 1px 0 ' + borderColor }}>
        <span className="text-[17px] font-bold" style={{ color: textPrimary }}></span>
        <div className="flex items-center gap-2">
          {/* 打卡按钮 */}
          {!isTeacher && (
            <button
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border-none cursor-pointer"
              style={{
                background: checkedIn ? (isDark ? 'rgba(32,191,107,0.15)' : '#F0FFF6') : (isDark ? 'rgba(108,99,255,0.15)' : '#EEF0FF'),
                color: checkedIn ? '#20BF6B' : '#6C63FF',
                transition: 'all 0.2s',
              }}
              onClick={async () => {
                if (checkedIn) { showToast('今日已打卡'); return }
                try {
                  await learnApi.checkIn()
                  setCheckedIn(true)
                  showToast('打卡成功！')
                } catch {
                  // 本地模拟
                  setCheckedIn(true)
                  showToast('打卡成功！')
                }
              }}
            >
              <i className={`fa-solid ${checkedIn ? 'fa-check-circle' : 'fa-calendar-check'}`} />
              {checkedIn ? '已打卡' : '打卡'}
            </button>
          )}

          {/* 身份切换按钮 */}
          <button
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border-none cursor-pointer"
            style={{ background: roleConfig.bg, color: roleConfig.color }}
            onClick={() => navigate('identity')}
          >
            <i className={`fa-solid ${roleConfig.icon}`} />
            {roleConfig.label}
            <i className="fa-solid fa-exchange-alt text-[10px]" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar" style={{ paddingBottom: '72px' }}>

        {/* 今日任务卡片 */}
        <div className="mx-4 mt-3 rounded-2xl p-4 text-white relative overflow-hidden"
             style={{ background: 'linear-gradient(135deg,#6C63FF 0%, #a29bfe 50%, #6C63FF 100%)', boxShadow: '0 8px 24px rgba(108,99,255,0.3)' }}>
          {/* 背景装饰 */}
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <div className="absolute -right-2 -bottom-8 w-20 h-20 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />

          {isTeacher ? (
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[15px] font-black">今日教学</span>
                <button
                  className="px-3 py-1 rounded-full text-xs font-medium border-none cursor-pointer"
                  style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}
                  onClick={() => navigate('homeworkCreate')}
                >
                  <i className="fa-solid fa-plus mr-1" />
                  发布作业
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { val: homeworks.length, label: '总作业', icon: 'fa-tasks' },
                  { val: submittedCount, label: '待批改', icon: 'fa-inbox' },
                  { val: gradedCount, label: '已批改', icon: 'fa-check-circle' },
                ].map((s, i) => (
                  <div key={i} className="bg-white bg-opacity-10 rounded-xl p-3 text-center backdrop-blur">
                    <i className={`fa-solid ${s.icon} text-sm mb-1 opacity-70`} />
                    <p className="text-xl font-black">{s.val}</p>
                    <p className="text-[10px] opacity-80">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : isStudent ? (
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[15px] font-black">今日学习</span>
                {pendingHomeworks.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: 'rgba(255,255,255,0.2)' }}>
                    {pendingHomeworks.length}个待完成
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { val: pendingHomeworks.length, label: '待完成', icon: 'fa-clock' },
                  { val: submittedCount, label: '已完成', icon: 'fa-check' },
                  { val: gradedCount, label: '已批改', icon: 'fa-award' },
                ].map((s, i) => (
                  <div key={i} className="bg-white bg-opacity-10 rounded-xl p-3 text-center backdrop-blur">
                    <i className={`fa-solid ${s.icon} text-sm mb-1 opacity-70`} />
                    <p className="text-xl font-black">{s.val}</p>
                    <p className="text-[10px] opacity-80">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="relative z-10">
              <span className="block text-[15px] font-black mb-1">今日学习</span>
              <span className="block text-xs opacity-85 mb-2">已坚持 <b>{stats?.streak ?? 0}</b> 天，继续加油！</span>
              <div className="h-1.5 rounded-full mb-1.5" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <div className="h-full rounded-full bg-white transition-all duration-500"
                     style={{ width: Math.min(((stats?.todayMinutes ?? 0) / (stats?.goalMinutes ?? 30)) * 100, 100) + '%' }} />
              </div>
              <span className="text-[11px] opacity-80">
                今日已学 <b>{(stats?.todayMinutes ?? 0)}分钟</b> / 目标 {(stats?.goalMinutes ?? 30)}分钟
              </span>
            </div>
          )}
        </div>

        {/* 待完成作业 - 学生 */}
        {isStudent && pendingHomeworks.length > 0 && (
          <Section title="待完成作业" actionLabel="查看全部" onAction={() => navigate('homeworkList')} textPrimary={textPrimary} textSecondary={textSecondary} shadowColor={shadowColor}>
            <div className="flex flex-col gap-3">
              {pendingHomeworks.slice(0, 3).map(h => (
                <HomeworkCard key={h.id} homework={h} showActions={false} />
              ))}
            </div>
          </Section>
        )}

        {/* 统计数据 */}
        <Section title={isTeacher ? '数据概览' : '学习数据'} textPrimary={textPrimary} textSecondary={textSecondary} shadowColor={shadowColor}>
          <div className="grid grid-cols-4 gap-2">
            {(isTeacher ? [
              { val: homeworks.length, label: '总作业', color: '#6C63FF' },
              { val: submittedCount, label: '提交数', color: '#3B82F6' },
              { val: gradedCount, label: '已批改', color: '#10B981' },
              { val: myCoursesData.length, label: '在学课程', color: '#F59E0B' },
            ] : [
              { val: stats?.streak ?? 0, label: '连续天数', color: '#6C63FF' },
              { val: submittedCount, label: '完成作业', color: '#3B82F6' },
              { val: gradedCount > 0 ? Math.round(gradedCount / Math.max(submittedCount,1) * 100) + '%' : '-', label: '优秀率', color: '#10B981' },
              { val: (stats?.totalHours ?? 0) + 'h', label: '累计学时', color: '#F59E0B' },
            ]).map((s, i) => (
              <div key={i} className="rounded-xl py-2.5 px-1 flex flex-col items-center gap-0.5 transition-transform hover:scale-105"
                   style={{ background: bgWhite, boxShadow: shadowColor }}>
                <span className="text-lg font-black" style={{ color: s.color }}>{s.val}</span>
                <span className="text-[10px] text-center leading-tight" style={{ color: textSecondary }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* 本周学习柱状图 */}
          {isStudent && (
            <div className="rounded-xl p-4 flex items-end justify-around gap-1.5 h-28 mt-3"
                 style={{ background: bgWhite, boxShadow: shadowColor }}>
              {(stats?.weekData ?? [0,0,0,0,0,0,0]).map((pct, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <span className="text-[9px] font-bold" style={{ color: i === todayIdx ? '#6C63FF' : textMuted }}>{pct}%</span>
                  <div className="w-full rounded-t-sm transition-all duration-500"
                       style={{
                         height: pct + '%',
                         minHeight: '3px',
                         background: i === todayIdx
                           ? 'linear-gradient(0deg,#6C63FF,#a29bfe)'
                           : (isDark ? 'rgba(108,99,255,0.25)' : '#E8EAFF'),
                         borderRadius: '3px 3px 0 0',
                       }} />
                  <span className="text-[9px]" style={{ color: i === todayIdx ? '#6C63FF' : textMuted, fontWeight: i === todayIdx ? '700' : '400' }}>
                    {WEEK_DAYS[i].replace('周','')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 快捷操作 - 老师 */}
        {isTeacher && (
          <Section title="快捷操作" textPrimary={textPrimary} textSecondary={textSecondary} shadowColor={shadowColor}>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: 'fa-tasks', color: '#6C63FF', label: '管理作业', sub: homeworks.length + '个作业', action: () => navigate('homeworkList') },
                { icon: 'fa-chart-pie', color: '#FF6B6B', label: '数据面板', sub: '教学分析', action: () => navigate('teacherDashboard') },
                { icon: 'fa-book', color: '#20BF6B', label: '课程管理', sub: myCoursesData.length + '个课程', action: () => navigate('courses') },
              ].map((btn, i) => (
                <button key={i} className="rounded-xl p-4 text-left border-none cursor-pointer transition-transform hover:scale-105"
                        style={{ background: bgWhite, boxShadow: shadowColor }}
                        onClick={btn.action}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                       style={{ background: isDark ? btn.color.replace(')',',0.15)') : btn.color.replace(')',',0.08)'), color: btn.color }}>
                    <i className={`fa-solid ${btn.icon} text-base`} />
                  </div>
                  <p className="text-sm font-bold" style={{ color: textPrimary }}>{btn.label}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: textSecondary }}>{btn.sub}</p>
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* 快捷工具 - 学生 */}
        {isStudent && (
          <Section title="快捷工具" textPrimary={textPrimary} textSecondary={textSecondary} shadowColor={shadowColor}>
            <div className="grid grid-cols-4 gap-2">
              {[
                { icon: 'fa-tasks', color: '#6C63FF', label: '我的作业', sub: pendingHomeworks.length > 0 ? `${pendingHomeworks.length}个待完成` : '全部完成', action: () => navigate('homeworkList') },
                { icon: 'fa-book', color: '#FF6B6B', label: '错题本', sub: '查缺补漏', action: () => navigate('mistakeBook') },
                { icon: 'fa-chart-line', color: '#6C63FF', label: '学习轨迹', sub: '进步可视化', action: () => navigate('learnTrack') },
                { icon: 'fa-brain', color: '#764ba2', label: 'AI推荐', sub: '个性化方案', action: () => navigate('aiRecommend') },
              ].map((btn, i) => (
                <button key={i} className="rounded-xl p-3 text-center border-none cursor-pointer transition-transform hover:scale-105"
                        style={{ background: bgWhite, boxShadow: shadowColor }}
                        onClick={btn.action}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2"
                       style={{ background: isDark ? btn.color.replace(')',',0.12)') : btn.color.replace(')',',0.06)'), color: btn.color }}>
                    <i className={`fa-solid ${btn.icon}`} style={{ fontSize: 16 }} />
                  </div>
                  <p className="text-xs font-bold" style={{ color: textPrimary }}>{btn.label}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: textSecondary }}>{btn.sub}</p>
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* 我的课程 */}
        <Section title="我的课程" textPrimary={textPrimary} textSecondary={textSecondary} shadowColor={shadowColor} noTopBorder>
          <div className="flex flex-col gap-2.5">
            {myCoursesData.map(c => {
              const id = c.courseId || c.id
              const title = c.title || '未知课程'
              const progress = c.progress || 0
              const currentLesson = c.currentLesson || c.chapter_id || 0
              const lessons = c.lessons || 0
              const img = c.image || c.img || ''
              return (
                <div key={id}
                        className="rounded-xl p-3 flex items-center gap-3 w-full text-left border-none cursor-pointer transition-transform hover:scale-[0.99] active:scale-[0.97]"
                        style={{ background: bgWhite, boxShadow: shadowColor }}
                        onClick={() => c.cat ? navigate('knowledge', null, null, null, { subject: c.cat, stage: c.stage || '全部' }) : navigate('detail', id)}>
                  <div className="rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ width: '52px', height: '52px', background: progressBg }}>
                    {img ? (
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <i className="fa-solid fa-play-circle" style={{ color: '#6C63FF', fontSize: 20 }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold truncate mb-0.5 flex-1" style={{ color: textPrimary }}>{title}</h4>
                      {c.cat && (
                        <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full"
                              style={{ background: isDark ? 'rgba(108,99,255,0.15)' : '#EEF0FF', color: '#6C63FF' }}>
                         知识点
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] mt-1 mb-1.5" style={{ color: textSecondary }}>上次学习：第 {currentLesson} 课 · 点击进入知识点练习</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: isDark ? 'rgba(108,99,255,0.15)' : '#EEF0FF' }}>
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: progress + '%', background: 'linear-gradient(90deg, #6C63FF, #a29bfe)' }} />
                      </div>
                      <span className="text-[11px] font-bold flex-shrink-0" style={{ color: '#6C63FF', minWidth: '36px' }}>{progress}%</span>
                    </div>
                    <span className="text-[10px] mt-0.5 block" style={{ color: textMuted }}>
                      第 {currentLesson}/{lessons} 课
                    </span>
                  </div>
                  {/* 右侧知识点图标指示 */}
                  {c.cat && (
                    <i className="fa-solid fa-book-open text-sm flex-shrink-0" style={{ color: '#6C63FF', opacity: 0.6 }} />
                  )}
                </div>
              )})}
            {myCoursesData.length === 0 && (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">📚</div>
                <p className="text-sm mb-2" style={{ color: textMuted }}>还没有在学课程</p>
                <p className="text-xs mb-4" style={{ color: textMuted }}>去课程中心看看，开始你的学习之旅吧</p>
                <button className="px-5 py-2 rounded-full border-none text-sm font-medium cursor-pointer" style={{ background: 'linear-gradient(135deg,#6C63FF,#a29bfe)', color: '#fff' }}
                        onClick={() => navigate('courses')}>
                  去选课
                </button>
              </div>
            )}
          </div>
        </Section>

        <div className="h-5" />
      </div>
    </div>
  )
}

function Section({ title, children, actionLabel, onAction, textPrimary = '#1a1a2e', textSecondary = '#636e72', textMuted = '#b2bec3', shadowColor, noTopBorder }) {
  const { isDark } = useTheme()
  return (
      <div className="mx-4 mt-4" style={{ borderTop: noTopBorder ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, paddingTop: noTopBorder ? 0 : 12 }}>
        {title && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-[15px] font-bold" style={{ color: textPrimary }}>{title}</span>
          {actionLabel && (
            <button className="text-xs border-none bg-transparent cursor-pointer" style={{ color: '#6C63FF', fontWeight: '600' }}
                    onClick={onAction}>
              {actionLabel} ›
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
